import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Me } from '../lib/api';

/**
 * Post-Stripe-checkout landing page. Two cases:
 *
 *   1) User is already signed in (rare, but possible if they enrolled while
 *      logged in on a device that already had a session). Show a "you're all
 *      set" message and route them to the dashboard.
 *
 *   2) User is NOT signed in. This is the normal case. They paid at Stripe
 *      checkout; the Stripe webhook created their user row and emailed them
 *      a 24-hour magic link. Tell them to check their email.
 *
 * We poll /api/auth/me briefly to detect case 1. If after a few tries there
 * is still no session, we assume case 2 (they need to click the email link).
 */
export default function WelcomePage({ onAuth }: { onAuth: () => Promise<void> }) {
  const [user, setUser] = useState<Me['user']>(null);
  const [checking, setChecking] = useState(true);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const me = await api.get<Me>('/api/auth/me');
        if (cancelled) return;
        setUser(me.user);
        if (me.user?.status === 'active') {
          await onAuth();
          setChecking(false);
          return;
        }
      } catch {
        // ignore
      }
      if (cancelled) return;
      if (tries < 3) {
        setTimeout(() => setTries((t) => t + 1), 1500);
      } else {
        setChecking(false);
      }
    }
    void poll();
    return () => {
      cancelled = true;
    };
  }, [tries, onAuth]);

  return (
    <div className="mx-auto max-w-xl py-16 text-center">
      <p className="font-mono text-xs uppercase tracking-widest text-amber">Payment received.</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight">
        Welcome to Constare Academy!
      </h1>

      {user?.status === 'active' ? (
        // Case 1: already signed in.
        <>
          <p className="mt-4 text-muted">
            You're all set. Week 1 is unlocked. New material drops one week from today.
          </p>
          <Link
            to="/dashboard"
            className="mt-8 inline-block rounded-md bg-amber px-5 py-2.5 font-semibold text-ink hover:bg-amber-hover"
            data-testid="link-open-dashboard"
          >
            Open your dashboard
          </Link>
        </>
      ) : checking ? (
        // Still polling.
        <div className="mt-8 rounded-md border border-line bg-surface p-5 text-sm text-muted">
          <p>Confirming your payment with Stripe. This usually takes a few seconds.</p>
        </div>
      ) : (
        // Case 2: check email.
        <div className="mt-8 rounded-md border border-line bg-surface p-6 text-left">
          <p className="text-text font-medium">Check your inbox to sign in.</p>
          <p className="mt-3 text-sm text-muted">
            We just emailed a sign-in link to the email you used at checkout. Click
            it to log in for the first time. The link stays valid for 24 hours.
          </p>
          <p className="mt-3 text-sm text-muted">
            Not seeing it? Check spam or promotions. If it's still missing after a
            few minutes, email{' '}
            <a
              href="mailto:marshall@constare.academy"
              className="text-amber hover:underline"
            >
              marshall@constare.academy
            </a>{' '}
            and we'll sort it out.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
            <Link
              to="/login"
              className="rounded-md border border-amber bg-amber/10 px-4 py-2 text-amber hover:bg-amber/20"
              data-testid="link-go-to-login"
            >
              Go to sign in
            </Link>
            <span className="text-muted">
              (or click the link in your email)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
